import React, { useState, useEffect, useMemo } from 'react';
import { tasksService } from '../../services';
import CommunicationLogger from './CommunicationLogger';
import TaskModal from './TaskModal';

// ─── Config ─────────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: '#6B7A94', bg: 'rgba(107,122,148,0.1)', border: 'rgba(107,122,148,0.3)' },
  medium: { label: 'Medium', color: '#C4871F', bg: 'rgba(196,135,31,0.1)', border: 'rgba(196,135,31,0.3)' },
  high: { label: 'High', color: '#DC2626', bg: 'rgba(220,38,38,0.1)', border: 'rgba(220,38,38,0.3)' },
  urgent: { label: 'Urgent', color: '#9333EA', bg: 'rgba(147,51,234,0.1)', border: 'rgba(147,51,234,0.3)' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#C4871F', bg: 'rgba(196,135,31,0.1)' },
  in_progress: { label: 'In Progress', color: '#2563EB', bg: 'rgba(37,99,235,0.1)' },
  completed: { label: 'Completed', color: '#059669', bg: 'rgba(5,150,105,0.1)' },
  cancelled: { label: 'Cancelled', color: '#6B7A94', bg: 'rgba(107,122,148,0.1)' },
};

const COMM_TYPE_ICONS = {
  email: '✉️', phone: '📞', text: '💬', meeting: '🤝', quote_message: '💼', other: '📝',
};

function leftBorderColor(task) {
  if (task.status === 'completed') return '#059669';
  if (task.urgency_status === 'overdue') return '#DC2626';
  if (task.priority === 'urgent') return '#9333EA';
  if (task.priority === 'high') return '#C4871F';
  return 'var(--border)';
}

function formatDue(dateString) {
  if (!dateString) return 'No due date';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function parseTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  try { return JSON.parse(tags); } catch { return []; }
}

// ─── Task card ──────────────────────────────────────────────────────────────

function TaskCard({ task, onComplete, onEdit, onDelete, onLogComm }) {
  const [expandLogs, setExpandLogs] = useState(false);
  const pc = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const sc = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const overdue = task.urgency_status === 'overdue';
  const dueSoon = task.urgency_status === 'due_soon';
  const tags = parseTags(task.tags);
  const commLogs = task.comm_logs || [];
  const clientName = [task.client_first_name, task.client_last_name].filter(Boolean).join(' ') || 'No client';

  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden transition-all hover:shadow-md"
      style={{ borderLeft: `4px solid ${leftBorderColor(task)}` }}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <button onClick={onComplete} disabled={task.status === 'completed'}
            className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all disabled:cursor-default"
            style={{
              borderColor: task.status === 'completed' ? '#059669' : 'var(--border)',
              backgroundColor: task.status === 'completed' ? '#059669' : 'transparent',
            }}>
            {task.status === 'completed' && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <span className={`text-sm font-bold text-[var(--foreground)] ${task.status === 'completed' ? 'line-through opacity-50' : ''}`}>
                {task.title}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                style={{ color: pc.color, backgroundColor: pc.bg, borderColor: pc.border }}>
                {pc.label}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: sc.color, backgroundColor: sc.bg }}>
                {sc.label}
              </span>
              {task.category && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-[var(--muted-foreground)]" style={{ backgroundColor: 'var(--muted)' }}>
                  {task.category}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-[var(--muted-foreground)] mb-1.5">
              <span className="flex items-center gap-1">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                {clientName}
              </span>
              {task.reminder_enabled === 1 && (
                <span className="flex items-center gap-1" style={{ color: 'var(--accent)' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                  Reminder set
                </span>
              )}
            </div>

            {task.description && (
              <p className="text-xs text-[var(--muted-foreground)] mb-2 leading-relaxed line-clamp-2">{task.description}</p>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1 text-xs font-medium" style={{ color: overdue ? '#DC2626' : dueSoon ? '#C4871F' : 'var(--muted-foreground)' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                {formatDue(task.due_date)}
                {overdue && <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest" style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: '#DC2626' }}>OVERDUE</span>}
                {dueSoon && !overdue && <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest" style={{ backgroundColor: 'rgba(196,135,31,0.1)', color: '#C4871F' }}>DUE SOON</span>}
              </span>

              {tags.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {tags.map(tag => (
                    <span key={tag} className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>#{tag}</span>
                  ))}
                </div>
              )}

              {commLogs.length > 0 && (
                <button onClick={() => setExpandLogs(p => !p)} className="flex items-center gap-1 text-[10px] font-semibold transition-colors" style={{ color: 'var(--accent)' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                  {commLogs.length} comm log{commLogs.length !== 1 ? 's' : ''}
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: expandLogs ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><polyline points="6 9 12 15 18 9" /></svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            {task.status !== 'completed' && (
              <button onClick={onComplete} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105" style={{ color: '#059669', backgroundColor: 'rgba(5,150,105,0.08)' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                Complete
              </button>
            )}
            <button onClick={onLogComm} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105" style={{ color: '#2563EB', backgroundColor: 'rgba(37,99,235,0.08)' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              Log
            </button>
            <button onClick={onEdit} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105" style={{ color: 'var(--foreground)', backgroundColor: 'var(--muted)' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
              Edit
            </button>
            <button onClick={onDelete} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105" style={{ color: '#DC2626', backgroundColor: 'rgba(220,38,38,0.06)' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
              Delete
            </button>
          </div>
        </div>

        {expandLogs && commLogs.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-2">
            {commLogs.map(log => (
              <div key={log.id} className="flex items-start gap-2.5 p-2.5 rounded-xl text-xs" style={{ backgroundColor: 'var(--muted)' }}>
                <span className="text-base flex-shrink-0">{COMM_TYPE_ICONS[log.communication_type] || '📝'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-[var(--foreground)]">{log.subject || '(no subject)'}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: log.direction === 'outbound' ? 'rgba(196,135,31,0.15)' : 'rgba(37,99,235,0.1)',
                        color: log.direction === 'outbound' ? '#C4871F' : '#2563EB',
                      }}>
                      {log.direction === 'outbound' ? '↑ Out' : '↓ In'}
                    </span>
                    {!!log.follow_up_required && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: '#DC2626' }}>
                        Follow-up
                      </span>
                    )}
                  </div>
                  {log.content && <p className="text-[var(--muted-foreground)] leading-relaxed">{log.content}</p>}
                  <p className="text-[var(--muted-foreground)] mt-0.5 opacity-70">
                    {new Date(log.communication_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {log.duration_minutes ? ` · ${log.duration_minutes}min` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Kanban column ──────────────────────────────────────────────────────────

function KanbanColumn({ title, color, tasks, onEdit, onLogComm }) {
  return (
    <div className="flex-1" style={{ minWidth: '220px', maxWidth: '300px' }}>
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)]">{title}</span>
        <span className="ml-auto text-xs font-bold text-[var(--muted-foreground)] bg-[var(--muted)] px-2 py-0.5 rounded-full">{tasks.length}</span>
      </div>
      <div className="space-y-2">
        {tasks.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-[var(--border)] p-4 text-center">
            <p className="text-xs text-[var(--muted-foreground)]">No tasks</p>
          </div>
        )}
        {tasks.map(task => {
          const pc = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
          const commLogs = task.comm_logs || [];
          return (
            <div key={task.id} className="bg-white rounded-xl border border-[var(--border)] p-3 cursor-pointer hover:shadow-sm transition-all"
              style={{ borderLeft: `3px solid ${leftBorderColor(task)}` }} onClick={() => onEdit(task)}>
              <p className="text-xs font-bold text-[var(--foreground)] mb-1 leading-tight">{task.title}</p>
              <p className="text-[10px] text-[var(--muted-foreground)] mb-2">{[task.client_first_name, task.client_last_name].filter(Boolean).join(' ') || 'No client'}</p>
              <div className="flex items-center justify-between">
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border" style={{ color: pc.color, backgroundColor: pc.bg, borderColor: pc.border }}>
                  {pc.label}
                </span>
                <div className="flex items-center gap-1">
                  {task.urgency_status === 'overdue' && <span className="text-[9px] font-black text-red-600">OVERDUE</span>}
                  {commLogs.length > 0 && <span className="text-[9px] font-bold text-[var(--muted-foreground)]">💬 {commLogs.length}</span>}
                  <button onClick={e => { e.stopPropagation(); onLogComm(task); }} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: '#2563EB', backgroundColor: 'rgba(37,99,235,0.08)' }}>
                    Log
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

const TasksDashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [showCommLogger, setShowCommLogger] = useState(false);
  const [commLoggerTask, setCommLoggerTask] = useState(null);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [filter]);

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filter === 'pending') filters.status = 'pending';
      if (filter === 'in_progress') filters.status = 'in_progress';
      if (filter === 'completed') filters.status = 'completed';
      if (filter === 'overdue') filters.overdue_only = 'true';
      if (filter === 'due_soon') filters.due_soon = 'true';
      if (filter === 'high_priority') filters.priority = 'high';

      const data = await tasksService.getTasks(filters);
      setTasks(data);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStats(await tasksService.getTaskStats());
    } catch (err) {
      console.error('Failed to fetch task statistics:', err);
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await tasksService.completeTask(taskId);
      showSuccess('Task marked as complete.');
      await fetchTasks();
      await fetchStats();
    } catch (err) {
      console.error('Failed to complete task:', err);
      setError('Failed to complete task. Please try again.');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await tasksService.deleteTask(taskId);
      showSuccess('Task deleted.');
      await fetchTasks();
      await fetchStats();
    } catch (err) {
      console.error('Failed to delete task:', err);
      setError('Failed to delete task. Please try again.');
    }
  };

  const handleTaskSaved = async () => {
    setShowTaskModal(false);
    setEditTask(null);
    showSuccess(editTask ? 'Task updated.' : 'Task created.');
    await fetchTasks();
    await fetchStats();
  };

  const TABS = [
    { id: 'all', label: 'All Tasks', count: stats.total_tasks || 0 },
    { id: 'pending', label: 'Pending', count: stats.pending_tasks || 0 },
    { id: 'in_progress', label: 'In Progress', count: stats.in_progress_tasks || 0 },
    { id: 'overdue', label: 'Overdue', count: stats.overdue_tasks || 0 },
    { id: 'due_soon', label: 'Due Soon', count: stats.due_soon_tasks || 0 },
    { id: 'high_priority', label: 'High Priority', count: stats.high_priority_tasks || 0 },
    { id: 'completed', label: 'Completed', count: stats.completed_tasks || 0 },
  ];

  const filteredTasks = useMemo(() => {
    if (!search) return tasks;
    const q = search.toLowerCase();
    return tasks.filter(t =>
      t.title?.toLowerCase().includes(q) ||
      `${t.client_first_name || ''} ${t.client_last_name || ''}`.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      parseTags(t.tags).some(tag => tag.toLowerCase().includes(q))
    );
  }, [tasks, search]);

  const sorted = useMemo(() => [...filteredTasks].sort((a, b) => {
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (b.status === 'completed' && a.status !== 'completed') return -1;
    const aOv = a.urgency_status === 'overdue'; const bOv = b.urgency_status === 'overdue';
    if (aOv && !bOv) return -1; if (bOv && !aOv) return 1;
    if (!a.due_date) return 1; if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  }), [filteredTasks]);

  const kanbanColumns = [
    { id: 'pending', title: 'Pending', color: '#C4871F', tasks: filteredTasks.filter(t => t.status === 'pending' && t.urgency_status !== 'overdue') },
    { id: 'in_progress', title: 'In Progress', color: '#2563EB', tasks: filteredTasks.filter(t => t.status === 'in_progress') },
    { id: 'overdue', title: 'Overdue', color: '#DC2626', tasks: filteredTasks.filter(t => t.urgency_status === 'overdue') },
    { id: 'completed', title: 'Completed', color: '#059669', tasks: filteredTasks.filter(t => t.status === 'completed') },
  ];

  const statCards = [
    { label: 'Total Tasks', value: stats.total_tasks || 0, color: 'var(--primary)', bg: 'rgba(26,43,74,0.08)' },
    { label: 'Pending', value: stats.pending_tasks || 0, color: '#C4871F', bg: 'rgba(196,135,31,0.1)' },
    { label: 'In Progress', value: stats.in_progress_tasks || 0, color: '#2563EB', bg: 'rgba(37,99,235,0.1)' },
    { label: 'Overdue', value: stats.overdue_tasks || 0, color: '#DC2626', bg: 'rgba(220,38,38,0.1)' },
    { label: 'Completed', value: stats.completed_tasks || 0, color: '#059669', bg: 'rgba(5,150,105,0.1)' },
  ];

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: 'var(--accent)' }}></div>
          <p className="mt-4 text-sm text-[var(--muted-foreground)]">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative min-w-0">
      {/* Toast */}
      {success && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white" style={{ backgroundColor: 'var(--primary)' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          {success}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>Tasks</h1>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">Manage client follow-ups, bookings, and communications.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <div className="flex items-center rounded-xl border border-[var(--border)] overflow-hidden bg-white">
            {['list', 'kanban'].map(v => (
              <button key={v} onClick={() => setView(v)}
                className="px-3 py-2 text-xs font-bold flex items-center gap-1.5 transition-all"
                style={view === v ? { backgroundColor: 'var(--primary)', color: 'white' } : { color: 'var(--muted-foreground)' }}>
                {v === 'list'
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
                  : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                }
                {v === 'list' ? 'List' : 'Board'}
              </button>
            ))}
          </div>
          <button onClick={() => { setEditTask(null); setShowTaskModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:brightness-105" style={{ backgroundColor: 'var(--primary)' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Create Task
          </button>
          <button onClick={() => { setCommLoggerTask(null); setShowCommLogger(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border-2 border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)] transition-all">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            Log Comm
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statCards.map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-[var(--border)] p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg flex-shrink-0"
              style={{ fontFamily: 'var(--font-display)', backgroundColor: s.bg, color: s.color }}>
              {s.value}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">{s.label}</p>
              {s.label === 'Overdue' && s.value > 0 && <p className="text-[10px] text-red-500 font-semibold">Needs attention</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Search + filter tabs */}
      <div className="space-y-3">
        <div className="relative">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="2" className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search tasks by title, client, description, or tag…"
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[rgba(196,135,31,0.25)] focus:border-[var(--accent)] transition-all" />
        </div>

        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setFilter(tab.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap flex-shrink-0 transition-all"
              style={filter === tab.id ? { backgroundColor: 'var(--primary)', color: 'white' } : { color: 'var(--muted-foreground)', backgroundColor: 'white', border: '1px solid var(--border)' }}>
              {tab.label}
              {tab.count > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black"
                  style={filter === tab.id
                    ? { backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }
                    : { backgroundColor: tab.id === 'overdue' ? 'rgba(220,38,38,0.12)' : 'var(--muted)', color: tab.id === 'overdue' ? '#DC2626' : 'var(--muted-foreground)' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* List view */}
      {view === 'list' && (
        <div className="space-y-3">
          {sorted.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[var(--border)] px-6 py-12 text-center">
              <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: 'var(--muted)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.5"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              </div>
              <p className="text-sm font-semibold text-[var(--muted-foreground)]">No tasks found</p>
              <p className="text-xs text-[var(--muted-foreground)] mt-1">{search ? 'Try a different search term' : 'Create a task to get started'}</p>
            </div>
          ) : sorted.map(task => (
            <TaskCard key={task.id} task={task}
              onComplete={() => handleComplete(task.id)}
              onEdit={() => { setEditTask(task); setShowTaskModal(true); }}
              onDelete={() => handleDelete(task.id)}
              onLogComm={() => { setCommLoggerTask(task); setShowCommLogger(true); }}
            />
          ))}
        </div>
      )}

      {/* Board view */}
      {view === 'kanban' && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {kanbanColumns.map(col => (
            <KanbanColumn key={col.id} title={col.title} color={col.color} tasks={col.tasks}
              onEdit={t => { setEditTask(t); setShowTaskModal(true); }}
              onLogComm={t => { setCommLoggerTask(t); setShowCommLogger(true); }}
            />
          ))}
        </div>
      )}

      {/* Task create/edit modal */}
      {showTaskModal && (
        <TaskModal task={editTask} onClose={() => { setShowTaskModal(false); setEditTask(null); }} onSaved={handleTaskSaved} />
      )}

      {/* Communication logger modal */}
      {showCommLogger && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,28,46,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CommunicationLogger
              clientId={commLoggerTask?.client_id}
              taskId={commLoggerTask?.id}
              workflowStageId={commLoggerTask?.workflow_stage_id}
              onClose={() => { setShowCommLogger(false); setCommLoggerTask(null); }}
              onSaved={async () => {
                setShowCommLogger(false);
                setCommLoggerTask(null);
                showSuccess('Communication logged.');
                await fetchTasks();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksDashboard;
